import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, setAccessToken, type AuthUserDto, type LoginResponseDto } from '@/shared/api'

/**
 * Prihlásený používateľ.
 *
 * Store drží iba to, čo je klientský stav (kto som, aké mám roly). Access token
 * zostáva v `shared/api/session` — je to detail prenosu, nie stav obrazovky,
 * a nesmie skončiť v devtools snapshote Pinie.
 */
/**
 * Stopa po prihlásení, ktorú vidí JavaScript.
 *
 * Samotná relácia stojí na `HttpOnly` cookie, do ktorej stránka nevidí — a bez
 * akejkoľvek stopy sa nedá rozlíšiť „odhlásený“ od „po F5“. Aplikácia sa preto
 * po načítaní pýtala servera vždy a odhlásenému návštevníkovi svietilo v
 * konzole 401 hneď na úvodnej obrazovke. Tento príznak nie je tajomstvo ani
 * oprávnenie: hovorí len to, že sa oplatí skúsiť reláciu obnoviť.
 */
const SESSION_HINT = 'maxdoors.relacia'

function rememberSession(exists: boolean): void {
	try {
		if (exists) {
			window.localStorage.setItem(SESSION_HINT, '1')
		} else {
			window.localStorage.removeItem(SESSION_HINT)
		}
	} catch {
		// Súkromné okno s vypnutým úložiskom — pokračuje sa bez stopy.
	}
}

function sessionMayExist(): boolean {
	try {
		return window.localStorage.getItem(SESSION_HINT) === '1'
	} catch {
		// Bez úložiska sa radšej skúsi obnoviť: horšie je nechať prihláseného
		// človeka na prihlasovacej obrazovke než jedno 401 v konzole.
		return true
	}
}

export const useAuthStore = defineStore('auth', () => {
	const user = ref<AuthUserDto | null>(null)
	const loading = ref(false)
	/** `true`, kým sa nedokončil prvý pokus o obnovenie relácie po načítaní stránky. */
	const initializing = ref(true)

	const isAuthenticated = computed(() => user.value !== null)
	const partnerId = computed(() => user.value?.partnerId ?? null)

	/** Autorizácia sa kontroluje na serveri; toto len skrýva položky menu. */
	function hasRole(role: string): boolean {
		return user.value?.roles.includes(role) ?? false
	}

	/**
	 * Aspoň jedna z rolí.
	 *
	 * Superadmin prejde vždy — je to servisný účet a skrývať pred ním
	 * obrazovky by len sťažovalo diagnostiku; dáta mu aj tak neotvorí nič
	 * iné než vedomé zrušenie rozsahu, ktoré sa zapisuje do auditu.
	 */
	function hasAnyRole(roles: readonly string[]): boolean {
		if (roles.length === 0) {
			return true
		}

		return hasRole('SUPERADMIN') || roles.some((role) => hasRole(role))
	}

	/** Používateľ patrí partnerovi, nie importérovi. */
	const isPartner = computed(() => user.value?.partnerId !== null && user.value?.partnerId !== undefined)

	async function login(email: string, password: string): Promise<void> {
		loading.value = true
		try {
			// `skipAuthRefresh`: 401 tu znamená zlé heslo, nie vypršanú reláciu.
			const response = await api.post<LoginResponseDto>(
				'/auth/login',
				{ email, password },
				{ skipAuthRefresh: true },
			)
			setAccessToken(response.accessToken)
			user.value = response.user
			rememberSession(true)
		} finally {
			loading.value = false
		}
	}

	/**
	 * Obnoví reláciu po načítaní stránky.
	 *
	 * Access token je len v pamäti, takže po F5 zostane iba refresh cookie —
	 * profil sa preto načíta znova. Zlyhanie nie je chyba: znamená odhlásený stav.
	 */
	async function bootstrap(): Promise<void> {
		if (!sessionMayExist()) {
			initializing.value = false

			return
		}

		try {
			user.value = await api.get<AuthUserDto>('/auth/me')
			rememberSession(true)
		} catch {
			user.value = null
			setAccessToken(null)
			rememberSession(false)
		} finally {
			initializing.value = false
		}
	}

	async function logout(): Promise<void> {
		try {
			await api.post<void>('/auth/logout', undefined, { skipAuthRefresh: true })
		} finally {
			clear()
		}
	}

	/** Volá sa aj pri strate relácie (401 po neúspešnom obnovení tokenu). */
	function clear(): void {
		user.value = null
		setAccessToken(null)
		rememberSession(false)
	}

	return {
		user,
		loading,
		initializing,
		isAuthenticated,
		partnerId,
		isPartner,
		hasRole,
		hasAnyRole,
		login,
		logout,
		bootstrap,
		clear,
	}
})
